import { TFunction, Trans, useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'

import { SomethingWentWrong } from '../../../shared/somethingWentWrong/SomethingWentWrong'

import {
  Accordion,
  Button,
  ConfirmationModal,
  Modal,
  Flex,
  Spacer,
  Loader,
  Typography,
  MarkdownPreview,
  AcknowledgeModal,
  SidePanel,
  Icon
} from '@percent/lemonade'
import { ReviewQuestion } from './reviewQuestion/ReviewQuestion'
import { MoneyFormField } from './money/MoneyFormField'
import { buildMoneyGroups, moneyRenderDecision } from './money/groupMoneyQuestions'
import { useTaskDraftAutosave, DraftValues, TaskDraftSaveStatus } from '@percent/domain/grants'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/partner-dashboard/common/hooks/useQuery/useQuery'
import {
  AWARD_QUESTION_TYPES,
  DATE_QUESTION_TYPES,
  CandidateFormQuestion,
  CandidateFormSelectOption
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import { startOfTodayUtc } from '@percent/partner-dashboard/app/dashboard/grants/utils/utils'
import { CandidateReviewProps } from './CandidateReview.types'
import { useMutation } from '@percent/partner-dashboard/common/hooks/useMutation/useMutation'
import { currencyMap, useMutation as useRQMutation, evaluateValidationRules } from '@percent/utility'
import type { CandidateData } from '@percent/utility'
import { useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import styles from './CandidateReview.module.scss'

export const MAX_TEXTAREA_INPUT_LEN = 5000
const USD_CURRENCY_MINOR_UNITS = currencyMap.USD.minorUnits
const JUST_NOW_THRESHOLD_MS = 60_000

const candidateReviewQuestionTypeValidator = (
  question: CandidateFormQuestion,
  t: TFunction,
  candidateData: CandidateData
) => {
  let validator: Yup.AnySchema = Yup.string()

  if (question.type === 'email') {
    validator = Yup.string().email(t('errorMessage.invalidEmail'))
  }

  if (question.type === 'textarea') {
    const maxLength = question.maxLength ?? MAX_TEXTAREA_INPUT_LEN
    validator = Yup.string().max(maxLength, t('grants.candidateForm.review.error.maxLength', { length: maxLength }))
  }

  if (question.type === 'score') {
    validator = Yup.number().min(0).max(question.maxScore)
  }

  if (AWARD_QUESTION_TYPES.includes(question.type)) {
    validator = Yup.number().min(0, t('errorMessage.postiveNumber', { value: 0 }))
  }

  if (question.type === 'number') {
    validator = Yup.number().typeError(t('errorMessage.invalidNumber'))
  }

  if (question.type === 'money') {
    if (question.key.endsWith('.currency')) {
      // Currency half: an ISO code string, narrowed to the question's allow-list when it carries one.
      if (question.allowedCurrencies?.length) {
        validator = validator.oneOf(question.allowedCurrencies, t('errorMessage.invalidCurrency'))
      }
    } else {
      // Amount half: an integer in minor units. Any min/max bounds are expressed as validationRules and
      // enforced by the generic validationRules pass below.
      validator = Yup.number().typeError(t('errorMessage.invalidNumber')).integer(t('errorMessage.invalidNumber'))
    }
  }

  if (question.type === 'select' && question.multiple) {
    const minItems = question.required ? 1 : 0
    validator = Yup.array().min(minItems, t('grants.candidateForm.review.error.required'))
  }

  if (question.type === 'documents') {
    const minFiles = question.required ? 1 : 0
    validator = Yup.array().min(minFiles, t('grants.candidateForm.review.error.required'))
  }

  if (DATE_QUESTION_TYPES.includes(question.type)) {
    if (question.type === 'payment_date') {
      validator = Yup.date().max(new Date(), t('grants.candidateForm.review.error.dateInPast'))
    } else if (question.type === 'scheduled_event') {
      validator = Yup.date().min(startOfTodayUtc(), t('grants.candidateForm.review.error.dateNotInPast'))
    } else {
      validator = Yup.date()
    }
  }

  if (question.required) {
    validator = validator.required(t('grants.candidateForm.review.error.required'))
  }

  if (question.validationRules?.length) {
    validator = validator.test('custom-validation', '', function (value) {
      if (value == null || value === '') return true
      const error = evaluateValidationRules(String(value), question.validationRules!, candidateData)

      return error ? this.createError({ message: error }) : true
    })
  }

  return validator
}

function computeVisibilityChanges(
  question: CandidateFormQuestion,
  value: string | number | string[] | File[]
): Record<string, boolean> {
  switch (question.type) {
    case 'select':
    case 'decision': {
      const options = question.options as CandidateFormSelectOption[]
      const selectedValues = question.type === 'select' && question.multiple ? (value as string[]) : [value as string]
      const visibility: Record<string, boolean> = {}

      for (const option of options) {
        // eslint-disable-next-line no-continue
        if (!option.conditionalQuestionIds?.length) continue

        const isSelected = selectedValues.includes(option.value)

        for (const condId of option.conditionalQuestionIds) {
          visibility[condId] = isSelected || (visibility[condId] ?? false)
        }
      }

      return visibility
    }
    default:
      return {}
  }
}

export function CandidateReview({
  organisationName,
  formId,
  taskId,
  onTaskSubmitted,
  autosaveEnabled = false,
  candidateData
}: Readonly<CandidateReviewProps>) {
  const { id: programId, candidateId } = useRequiredParams()
  const [modalType, setModalType] = useState<'confirmation' | 'success' | 'error' | null>(null)
  const [hasDocuments, setHasDocuments] = useState(false)

  const { grantsService } = useServices()
  const { t, i18n } = useTranslation()

  const { status, lastSavedAt, initialDraft, scheduleSave, clearDraft } = useTaskDraftAutosave<DraftValues>({
    candidateId: candidateId ?? '',
    taskId,
    enabled: autosaveEnabled
  })

  const [{ data: candidateFormData, isLoading: isLoadingCandidateForm, errorMessage: candidateFormErrorMessage }] =
    useQuery(grantsService.getCandidateReviewForm, { programId, candidateId, formId })

  const candidateForm = candidateFormData?.candidateForm

  const sortedPages = useMemo(
    () => candidateForm?.pages?.slice()?.sort((a, b) => a.order - b.order) ?? [],
    [candidateForm?.pages]
  )

  const reviewerQuestions = sortedPages.flatMap(page => page.questions)

  const [visibleQuestions, setVisibleQuestions] = useState<Record<string, boolean>>({})

  const [{ isLoading }, { apiFunc: postCandidateForm }] = useMutation(
    grantsService.postCandidateForm,
    () => {
      clearDraft()
      setModalType('success')
    },
    () => setModalType('error')
  )

  const [{ isPending: isUploadDocumentsPending }, { mutateAsync: uploadDocuments }] = useRQMutation(
    grantsService.uploadCandidateDocuments,
    // Do not set success as this is just a part of the submission. If the form fails the documents will be deleted according to the S3 policy
    undefined,
    () => setModalType('error')
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        reviewerQuestions.reduce((acc, question) => {
          if (question.hidden && !visibleQuestions[question.id]) {
            acc[question.id] = Yup.mixed().notRequired()
          } else {
            acc[question.id] = candidateReviewQuestionTypeValidator(question, t, candidateData)
          }

          return acc
        }, {} as Record<string, Yup.AnySchema>)
      ),
    [reviewerQuestions, visibleQuestions, t, candidateData]
  )

  const { handleSubmit, values, setFieldValue, errors, touched, setTouched, validateForm } = useFormik({
    initialValues: reviewerQuestions.reduce((acc, question) => {
      const draftValue = initialDraft?.[question.id]

      if (question.type === 'documents' || (question.type === 'select' && question.multiple)) {
        acc[question.id] = Array.isArray(draftValue) ? draftValue : []
      } else if (question.type === 'money' && question.key.endsWith('.currency')) {
        // Seed the currency half so a submission always carries a currency.
        acc[question.id] =
          typeof draftValue === 'string' && draftValue !== '' ? draftValue : question.defaultCurrency ?? 'USD'
      } else {
        acc[question.id] = draftValue ?? ''
      }

      return acc
    }, {} as Record<string, string | number | string[] | File[]>),
    validationSchema,
    onSubmit: async formValues => {
      const hasDocumentsValue = reviewerQuestions.some(
        question => question.type === 'documents' && (formValues[question.id] as File[])?.length > 0
      )
      setHasDocuments(hasDocumentsValue)

      const visibleEntries = Object.entries(formValues).filter(([questionId]) => {
        const question = reviewerQuestions.find(q => q.id === questionId)

        return !question?.hidden || visibleQuestions[questionId]
      })

      const formAnswers = await Promise.all(
        visibleEntries.map(async ([questionId, answer]) => {
          const question = reviewerQuestions.find(q => q.id === questionId)
          const isDocumentsQuestion = question?.type === 'documents'
          const isAwardAmountQuestion = question?.type && AWARD_QUESTION_TYPES.includes(question?.type)

          if (isDocumentsQuestion) {
            const formData = new FormData()
            const files = answer as File[]

            if (files.length === 0) {
              return { questionId, answer: [] }
            }

            files.forEach((file, index) => formData.append(`file${index}`, file))
            const { s3Keys } = await uploadDocuments({ candidateId: candidateId ?? '', formData })

            return { questionId, answer: s3Keys }
          }

          if (question?.type === 'select' && question.multiple) {
            return { questionId, answer: answer as string[] }
          }

          if (isAwardAmountQuestion) {
            return { questionId, answer: String(Number(answer) * 10 ** USD_CURRENCY_MINOR_UNITS) }
          }

          if (question?.type === 'score' && answer !== '' && question.scoreWeight) {
            return { questionId, answer: String(Number(answer) * question.scoreWeight) }
          }

          return { questionId, answer: String(answer) }
        })
      )

      const filteredFormAnswers = formAnswers.filter(({ answer }) => !Array.isArray(answer) || answer.length > 0)

      postCandidateForm({ programId, candidateId, taskId, formAnswers: filteredFormAnswers })
    },
    enableReinitialize: true,
    validateOnMount: true
  })

  useEffect(() => {
    validateForm()
  }, [visibleQuestions, validateForm])

  const hasRestoredVisibility = useRef(false)

  useEffect(() => {
    if (hasRestoredVisibility.current || !initialDraft || reviewerQuestions.length === 0) {
      return
    }

    hasRestoredVisibility.current = true

    const restoredVisibility = reviewerQuestions.reduce((acc, question) => {
      const draftValue = initialDraft[question.id]

      return draftValue === undefined ? acc : { ...acc, ...computeVisibilityChanges(question, draftValue) }
    }, {} as Record<string, boolean>)

    if (Object.keys(restoredVisibility).length > 0) {
      setVisibleQuestions(prev => ({ ...prev, ...restoredVisibility }))
    }
  }, [initialDraft, reviewerQuestions])

  const closeModal = () => setModalType(null)

  const handleSubmitClick = async () => {
    const validationErrors = await validateForm()

    if (Object.keys(validationErrors).length > 0) {
      setTouched(
        Object.keys(validationErrors).reduce((acc, questionId) => ({ ...acc, [questionId]: true }), {
          ...touched
        })
      )

      return
    }

    setModalType('confirmation')
  }

  const persistDraft = (nextValues: Record<string, string | number | string[] | File[]>) =>
    scheduleSave(
      Object.fromEntries(
        Object.entries(nextValues).filter(
          ([questionId]) => reviewerQuestions.find(q => q.id === questionId)?.type !== 'documents'
        )
      ) as DraftValues
    )

  const renderSavedLabel = useCallback(
    (savedAt: number) =>
      Date.now() - savedAt < JUST_NOW_THRESHOLD_MS
        ? t('grants.tasks.autoSaveStatus.savedJustNow')
        : t('grants.tasks.autoSaveStatus.saved', {
            relativeTime: formatDistanceToNow(savedAt, { addSuffix: true })
          }),
    [t]
  )

  const handleChange = (questionId: string, value: string | number | string[] | File[]) => {
    const question = reviewerQuestions.find(q => q.id === questionId)
    const visibilityChanges = question ? computeVisibilityChanges(question, value) : {}

    if (Object.keys(visibilityChanges).length > 0) {
      setVisibleQuestions(prev => ({ ...prev, ...visibilityChanges }))

      const nextTouched = { ...touched, [questionId]: true } as Record<string, boolean>

      for (const [condId, visible] of Object.entries(visibilityChanges)) {
        if (!visible) {
          const condQuestion = reviewerQuestions.find(q => q.id === condId)

          if (condQuestion) {
            setFieldValue(condId, condQuestion.type === 'documents' ? [] : '')
            nextTouched[condId] = false
          }
        }
      }

      setTouched(nextTouched)
    }

    setTouched({ ...touched, [questionId]: true })
    setFieldValue(questionId, value)
    persistDraft({ ...values, [questionId]: value })
  }

  const successModal = modalType === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.candidateForm.review.confirmationModal.success.title')}
      description={
        <Trans
          i18nKey="grants.candidateForm.review.confirmationModal.success.description"
          components={{
            b: <b />
          }}
          values={{ organisation: organisationName }}
        />
      }
      buttonText={t('button.close')}
      handleClose={() => {
        closeModal()
        onTaskSubmitted(hasDocuments)
      }}
      viewTestId="review-success-modal"
      buttonTestId="close-review-success-modal"
    />
  )

  const errorModal = modalType === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.candidateForm.review.confirmationModal.error.title')}
      description={
        <Trans
          i18nKey="grants.candidateForm.review.confirmationModal.error.description"
          components={{
            b: <b />
          }}
          values={{ organisation: organisationName }}
        />
      }
      buttonText={t('button.done')}
      handleClose={closeModal}
      viewTestId="review-error-modal"
      buttonTestId="close-review-error-modal"
    />
  )

  const getVisiblePageQuestions = useCallback(
    (page: NonNullable<typeof candidateForm>['pages'][number]) =>
      page.questions
        ?.filter(question => !question.hidden || visibleQuestions[question.id])
        ?.sort((a, b) => a.order - b.order) ?? [],
    [visibleQuestions]
  )

  const renderPageQuestions = useCallback(
    (page: NonNullable<typeof candidateForm>['pages'][number]) => {
      const moneyGroups = buildMoneyGroups(page.questions)

      return getVisiblePageQuestions(page).map(question => {
        const money = moneyRenderDecision(question, moneyGroups)

        if (money) {
          if (!money.render) return null

          return (
            <MoneyFormField
              key={question.id}
              amountQuestion={money.group.amount}
              currencyQuestion={money.group.currency}
              currencyOnly={money.currencyOnly}
              values={values}
              errors={errors as Record<string, string | undefined>}
              touched={touched as Record<string, boolean | undefined>}
              onChange={handleChange}
              locale={i18n.language || 'en-US'}
            />
          )
        }

        return (
          <ReviewQuestion
            key={question.id}
            question={question}
            handleChange={handleChange}
            value={values?.[question.id]}
            error={errors?.[question.id]}
            touched={touched?.[question.id]}
          />
        )
      })
    },
    [getVisiblePageQuestions, handleChange, values, errors, touched, i18n.language]
  )

  return (
    <>
      {candidateFormErrorMessage && (
        <SomethingWentWrong
          title="grants.somethingWentWrong.title"
          description="grants.somethingWentWrong.description.formNotFound"
          hideWrapper
        />
      )}
      <Flex direction="column" gap={16}>
        {isLoadingCandidateForm && <Loader />}
        {!isLoadingCandidateForm && !candidateFormErrorMessage && (
          <>
            <Typography variant="h4">{candidateForm?.displayName}</Typography>
            {sortedPages.length > 1 ? (
              <Accordion
                multiple
                defaultValue={[sortedPages[0].id]}
                chevron={<Icon name="chevron-down" />}
                classNames={{
                  item: styles.pageWrapper,
                  control: styles.pageControl,
                  content: styles.pageContent
                }}
              >
                {sortedPages.map(page => (
                  <Accordion.Item key={page.id} value={page.id}>
                    <Accordion.Control>
                      <Typography variant="h5">
                        {page.title}{' '}
                        <span className={styles.questionCount}>
                          {t('grants.candidateForm.review.page.questionCount', {
                            count: getVisiblePageQuestions(page).length
                          })}
                        </span>
                      </Typography>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Flex direction="column" gap={16}>
                        {page.description && (
                          <MarkdownPreview content={page.description} className={styles.description} />
                        )}
                        {renderPageQuestions(page)}
                      </Flex>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              sortedPages.map(page => (
                <Flex key={page.id} direction="column" gap={16}>
                  {page.title !== candidateForm?.displayName && <Typography variant="h5">{page.title}</Typography>}
                  {page.description && <MarkdownPreview content={page.description} className={styles.description} />}
                  {renderPageQuestions(page)}
                </Flex>
              ))
            )}
            <SidePanel.Footer justify="space-between" align="center">
              <span>
                {autosaveEnabled && (
                  <TaskDraftSaveStatus
                    status={status}
                    lastSavedAt={lastSavedAt}
                    savingLabel={t('grants.tasks.autoSaveStatus.saving')}
                    errorLabel={t('grants.tasks.autoSaveStatus.error')}
                    renderSavedLabel={renderSavedLabel}
                  />
                )}
              </span>
              <Button onClick={handleSubmitClick} data-testid="submit-review-button">
                {t('grants.tasks.submitReview')}
              </Button>
            </SidePanel.Footer>
          </>
        )}
      </Flex>

      {modalType && (
        <Modal open={!!modalType} onClose={closeModal}>
          {successModal || errorModal || (
            <ConfirmationModal
              title={t('grants.candidateForm.review.confirmationModal.title')}
              loading={isLoading || isUploadDocumentsPending}
              primaryButtonText={t('grants.tasks.submitReview')}
              primaryBtnTestId="submit-review-button-confirmation"
              secondaryButtonText={t('button.cancel')}
              handleSubmit={handleSubmit}
              handleClose={closeModal}
            >
              <Trans
                i18nKey="grants.candidateForm.review.confirmationModal.description"
                components={{
                  b: <b />,
                  br: <Spacer axis="vertical" size={4} />
                }}
                values={{
                  organisation: organisationName
                }}
              />
            </ConfirmationModal>
          )}
        </Modal>
      )}
    </>
  )
}
